import React, { useState, useEffect } from "react";
import {
  FileText,
  Plus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileSignature,
  ClipboardList,
  Users,
  Send,
} from "lucide-react";
import Button from "../../../components/common/ui/button/Button";
import Badge from "../../../components/common/ui/badge/Badge";
import { useAlert } from "../../../hooks/useAlert";
import { Table, TableColumn } from "../../../components/common/tables/Table";
import { TableContainer } from "../../../components/common/tables/TableContainer";
import {
  IconWithTextCell,
  DateCell,
  TextCell,
  ActionsCell,
} from "../../../components/common/tables/cells";
import { apiClient } from "../../../utils/apiClient/index.ts";
import { useOrg } from "../../../contexts/OrgContext";
import { useToast } from "../../../contexts/ToastContext.tsx";
import { Request, RequestStatus, RequestType } from "@secure-caseworker/shared";

interface RequestsTabProps {
  caseId: string;
}

export const RequestsTab: React.FC<RequestsTabProps> = ({ caseId }) => {
  const { setAlert } = useAlert();
  const { showToast } = useToast();
  const { navigateOrg } = useOrg();
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRequests();
  }, [caseId]);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getRequestsByCase(caseId);
      setRequests(data as Request[]);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: "Failed to load requests",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId: string) => {
    if (!confirm("Are you sure you want to cancel this request?")) return;

    try {
      await apiClient.cancelRequest(requestId);

      showToast("Request canceled successfully");

      loadRequests();
    } catch (error) {
      showToast("Failed to cancel request", "error");
    }
  };

  const handleResend = async (requestId: string) => {
    try {
      await apiClient.resendRequest(requestId);
      showToast("Request resent");
    } catch (error) {
      console.log(error);
      showToast("Failed to resend", "error");
    }
  };

  const getStatusBadge = (status: RequestStatus) => {
    switch (status) {
      case "pending":
        return (
          <Badge color="warning">
            <Clock size={14} className="mr-1" /> Pending
          </Badge>
        );
      case "sent":
        return (
          <Badge color="info">
            <Send size={14} className="mr-1" /> Sent
          </Badge>
        );
      case "in_progress":
        return (
          <Badge color="info">
            <AlertCircle size={14} className="mr-1" /> In Progress
          </Badge>
        );
      case "completed":
        return (
          <Badge color="success">
            <CheckCircle size={14} className="mr-1" /> Completed
          </Badge>
        );
      case "canceled":
        return (
          <Badge color="error">
            <XCircle size={14} className="mr-1" /> Canceled
          </Badge>
        );
      default:
        return <Badge color="light">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: RequestType) => {
    const styles =
      type === "signature"
        ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
        : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400";
    const Icon = type === "signature" ? FileSignature : ClipboardList;
    const label = type === "signature" ? "Signature" : "Form";
    return (
      <span
        className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${styles}`}
      >
        <Icon size={12} />
        {label}
      </span>
    );
  };

  const getParticipantsSummary = (request: Request): string => {
    if (
      !request.request_participants ||
      request.request_participants.length === 0
    ) {
      return "No participants";
    }

    const total = request.request_participants.length;
    const completed = request.request_participants.filter(
      (p) => p.status === "completed"
    ).length;

    return `${completed}/${total} completed`;
  };

  const getTypeIcon = (type: RequestType) => {
    return type === "signature" ? FileSignature : ClipboardList;
  };

  const columns: TableColumn<Request>[] = [
    {
      header: "Type",
      accessor: "request_template",
      render: (_value, row) =>
        getTypeBadge(row.template?.type || RequestType.FORM),
    },
    {
      header: "Template",
      accessor: "request_template",
      render: (_value, row) => (
        <IconWithTextCell
          icon={getTypeIcon(row.template?.type || RequestType.FORM)}
          iconColor={
            row.template?.type === RequestType.SIGNATURE ? "purple" : "brand"
          }
          primaryText={row.template?.title || "Unknown Template"}
        />
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (value) => getStatusBadge(value),
    },
    {
      header: "Participants",
      accessor: "request_participants",
      render: (_value, row) => (
        <div className="flex items-center gap-2">
          <Users size={14} className="text-gray-400" />
          <TextCell>{getParticipantsSummary(row)}</TextCell>
        </div>
      ),
    },
    {
      header: "Due Date",
      accessor: "due_date",
      render: (value) => (
        <DateCell date={value || ""} format="date" fallback="No due date" />
      ),
    },
    {
      header: "Created",
      accessor: "created_at",
      render: (value) => <DateCell date={value} format="datetime" />,
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_value, row) => (
        <ActionsCell
          items={[
            {
              label: "View Details",
              icon: <FileText className="w-4 h-4" />,
              onClick: () => {
                navigateOrg(`/cases/${caseId}/requests/${row.id}`);
              },
            },
            ...(row.status === "pending" ||
            row.status === "sent" ||
            row.status === "in_progress"
              ? [
                  {
                    label: "Resend Request",
                    icon: <Send className="w-4 h-4" />,
                    onClick: () => handleResend(row.id),
                  },
                ]
              : []),
            {
              label: "",
              separator: true,
              onClick: () => {},
            },
            ...(row.status !== "completed" && row.status !== "canceled"
              ? [
                  {
                    label: "Cancel Request",
                    icon: <XCircle className="w-4 h-4" />,
                    onClick: () => handleCancelRequest(row.id),
                    danger: true,
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <TableContainer
        loading={loading}
        loadingText="Loading requests..."
        isEmpty={requests.length === 0}
        emptyIcon={<FileText size={48} />}
        emptyTitle="No requests yet"
        emptyDescription="Initiate a request to collect forms or signatures from participants"
        emptyAction={{
          label: "Initiate New Request",
          icon: <Plus size={18} />,
          onClick: () => navigateOrg(`/cases/${caseId}/requests/new`),
        }}
        title="Requests"
        titleIcon={<FileText size={20} />}
        count={requests.length}
        headerAction={
          <Button
            onClick={() => navigateOrg(`/cases/${caseId}/requests/new`)}
            startIcon={<Plus size={18} />}
          >
            Initiate Request
          </Button>
        }
      >
        <Table columns={columns} data={requests} />
      </TableContainer>
    </div>
  );
};

export default RequestsTab;
